// src/contexts/PermissionsContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [canEdit, setCanEdit] = useState(true);
  const [canExecuteCommand, setCanExecuteCommand] = useState(true);

  const setPermissions = useCallback(({ canEdit: edit, canExecuteCommand: execute }) => {
    setCanEdit(typeof edit === "boolean" ? edit : true);
    setCanExecuteCommand(typeof execute === "boolean" ? execute : true);

    console.info("[PermissionsProvider] Permissões aplicadas", {
      canEdit: typeof edit === "boolean" ? edit : true,
      canExecuteCommand: typeof execute === "boolean" ? execute : true,
    });
  }, []);

  const resetPermissions = useCallback(() => {
    setCanEdit(true);
    setCanExecuteCommand(true);
  }, []);

  useDisableSaveButtons(canEdit);
  useDisableDeleteActions(canExecuteCommand);

  return (
    <PermissionsContext.Provider
      value={{
        canEdit,
        canExecuteCommand,
        setPermissions,
        resetPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

const SAVE_ACTION_TERMS = [
  "salvar",
  "Salvar",
  "registrar",
  "registrando",
  "salvar novo",
  "salvando",
  "inserir",
  "redefinir",
  "inserindo",
  "atualizar área",
  "importar",
  "import",
  "save",
  "saving",
  "incluir",
  "incluindo",
  "update",
  "updating",
  "excluir",
  "apagar",
  "apagando",
  "excluindo",
  "deletar",
  "deletando",
  "remover",
  "removendo",
  "ok",
  "confirmar",
  "confirmando",
  "concluir",
  "concluindo",
  "manutenção",
];

const DELETE_ACTION_TERMS = [
  "excluir",
  "excluindo",
  "delete",
  "deletar",
  "deletando",
  "remover",
  "removendo",
  "remove",
  "apagar",
  "apagando",
  "trash",
];

const ACTION_SELECTOR = [
  "button",
  "a",
  "input[type='button']",
  "input[type='submit']",
  "[role='button']",
  "[data-delete-action='true']",
  "svg",
  "[title]",
  "[aria-label]",
].join(",");

const getActionText = (element) =>
  [
    element.textContent,
    element.value,
    element.getAttribute("title"),
    element.getAttribute("aria-label"),
    element.getAttribute("name"),
    element.getAttribute("data-testid"),
    element.getAttribute("data-delete-action"),
    element.getAttribute("class"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

function useDisableSaveButtons(canEdit) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const isSaveButton = (element) =>
      SAVE_ACTION_TERMS.some((term) => getActionText(element).includes(term));

    const applyRule = () => {
      document.querySelectorAll(ACTION_SELECTOR).forEach((element) => {
        const guarded = element.getAttribute("data-can-edit-guarded") === "true";

        if (canEdit) {
          if (guarded) {
            const wasDisabled = element.getAttribute("data-original-disabled") === "true";

            if (!wasDisabled) {
              element.removeAttribute("disabled");
              element.removeAttribute("aria-disabled");
            }

            element.style.pointerEvents = "";
            element.removeAttribute("data-can-edit-guarded");
            element.removeAttribute("data-original-disabled");
          }
          return;
        }

        if (!isSaveButton(element)) return;

        if (!guarded) {
          element.setAttribute(
            "data-original-disabled",
            element.hasAttribute("disabled") ? "true" : "false"
          );
        }

        if (!element.hasAttribute("disabled")) {
          element.setAttribute("disabled", "true");
        }
        if (element.getAttribute("aria-disabled") !== "true") {
          element.setAttribute("aria-disabled", "true");
        }
        if (element.style.pointerEvents !== "none") {
          element.style.pointerEvents = "none";
        }
        if (!guarded) {
          element.setAttribute("data-can-edit-guarded", "true");
        }
      });
    };

    applyRule();

    const observer = new MutationObserver(applyRule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-label", "title", "value"],
    });

    return () => observer.disconnect();
  }, [canEdit]);
}

function useDisableDeleteActions(canExecuteCommand) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const isDeleteAction = (element) =>
      element.getAttribute("data-delete-action") === "true" ||
      DELETE_ACTION_TERMS.some((term) => getActionText(element).includes(term));

    const resetElement = (element) => {
      const guarded =
        element.getAttribute("data-can-execute-delete-guarded") === "true";

      if (!guarded) return;

      const wasDisabled =
        element.getAttribute("data-original-command-disabled") === "true";

      if (!wasDisabled) {
        element.removeAttribute("disabled");
        element.removeAttribute("aria-disabled");
      }

      element.style.pointerEvents = "";
      element.removeAttribute("data-can-execute-delete-guarded");
      element.removeAttribute("data-original-command-disabled");
    };

    const guardElement = (element) => {
      const guarded =
        element.getAttribute("data-can-execute-delete-guarded") === "true";

      if (!guarded) {
        element.setAttribute(
          "data-original-command-disabled",
          element.hasAttribute("disabled") ? "true" : "false"
        );
      }

      if (!element.hasAttribute("disabled")) {
        element.setAttribute("disabled", "true");
      }
      if (element.getAttribute("aria-disabled") !== "true") {
        element.setAttribute("aria-disabled", "true");
      }
      if (element.style.pointerEvents !== "none") {
        element.style.pointerEvents = "none";
      }
      if (!guarded) {
        element.setAttribute("data-can-execute-delete-guarded", "true");
      }
    };

    const applyRule = () => {
      document.querySelectorAll(ACTION_SELECTOR).forEach((element) => {
        if (canExecuteCommand) {
          resetElement(element);
          return;
        }

        if (isDeleteAction(element)) {
          guardElement(element);
        }
      });
    };

    const blockDeleteClick = (event) => {
      if (canExecuteCommand) return;

      let target = event.target;
      while (target && target !== document.body) {
        if (target.matches?.(ACTION_SELECTOR) && isDeleteAction(target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        target = target.parentElement;
      }
    };

    applyRule();
    document.addEventListener("click", blockDeleteClick, true);

    const observer = new MutationObserver(applyRule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        "class",
        "disabled",
        "aria-label",
        "title",
        "value",
        "data-testid",
      ],
    });

    return () => {
      document.removeEventListener("click", blockDeleteClick, true);
      observer.disconnect();
    };
  }, [canExecuteCommand]);
}
